import { FC, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Typography,
  FormControl,
} from '@mui/material';
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  ParsedCountry,
  usePhoneInput,
} from 'react-international-phone';
import { Text } from '@concepta/react-material-ui';
import 'react-international-phone/style.css';

interface Props {
  label: string;
  value: string;
  onChange: (phone: string, country: ParsedCountry) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  defaultCountry?: CountryIso2;
  name: string;
  disableDialCodePrefill?: boolean;
}

const InternationalPhoneInput: FC<Props> = ({
  label,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  defaultCountry = 'us',
  name,
  disableDialCodePrefill = false,
}) => {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry,
      value,
      countries: defaultCountries,
      // Only disable dial code prefill when explicitly requested (for optional fields)
      disableDialCodePrefill:
        disableDialCodePrefill && (!value || value.trim() === ''),
      onChange: data => {
        onChange(data.phone, data.country);
      },
    });

  useEffect(() => {
    if (defaultCountry && country.iso2 !== defaultCountry) {
      setCountry(defaultCountry);
    }
  }, [defaultCountry, setCountry]);

  return (
    <FormControl fullWidth error={!!error}>
      <TextField
        variant="standard"
        label={
          <>
            {label}
            {required && (
              <Text fontSize={12} display="inline" sx={{ ml: 0.5 }}>
                (required)
              </Text>
            )}
          </>
        }
        value={inputValue}
        onChange={handlePhoneValueChange}
        type="tel"
        name={name}
        inputRef={inputRef}
        disabled={disabled}
        error={!!error}
        helperText={error}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              style={{ marginRight: '8px', marginLeft: '-8px' }}
            >
              <Select
                MenuProps={{
                  style: {
                    height: '300px',
                    width: '360px',
                    top: '10px',
                    left: '-34px',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  width: 'max-content',
                  fieldset: {
                    display: 'none',
                  },
                  '&.Mui-focused:has(div[aria-expanded="false"])': {
                    fieldset: {
                      display: 'block',
                    },
                  },
                  '.MuiSelect-select': {
                    padding: '4px 8px',
                    paddingRight: '24px !important',
                  },
                  svg: {
                    right: 0,
                  },
                }}
                value={country.iso2}
                onChange={e => setCountry(e.target.value as CountryIso2)}
                disabled={disabled}
                renderValue={value => (
                  <FlagImage iso2={value} style={{ display: 'flex' }} />
                )}
              >
                {defaultCountries.map(c => {
                  const countryData = parseCountry(c);
                  return (
                    <MenuItem key={countryData.iso2} value={countryData.iso2}>
                      <FlagImage
                        iso2={countryData.iso2}
                        style={{ marginRight: '8px' }}
                      />
                      <Typography marginRight="8px">
                        {countryData.name}
                      </Typography>
                      <Typography color="gray">
                        +{countryData.dialCode}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </InputAdornment>
          ),
        }}
        inputProps={{
          'aria-label': label,
          'aria-required': required,
          'aria-invalid': !!error,
        }}
      />
    </FormControl>
  );
};

export default InternationalPhoneInput;
